import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiOutlineRollback, AiOutlineSearch, AiOutlineReload } from 'react-icons/ai';
import myAxios from '../AxiosUrl';
import styles from './Returns.module.css';

/**
 * Orders → Returns (2026-10-31). Parcels coming back from the courier: an
 * order marked Return (by hand, bulk, or Steadfast when it cancels) waits
 * here until the shop receives it and marks it Good (stock stays back) or
 * Damaged (stock taken out again).
 */

const tk = (n) => `৳${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const day = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const COURIER = { cancelled: 'Steadfast: returning', delivered: 'Steadfast: delivered', partial_delivered: 'Steadfast: part delivered', pending: 'Steadfast: on the way', in_review: 'Steadfast: in review', hold: 'Steadfast: on hold' };
const STATUS = { 1: 'New', 2: 'Call Done', 3: 'Courier Done', 4: 'Print Done', 5: 'Cancel', 6: 'Return', 7: 'Call Not Received', 8: 'Ask Advance', 9: 'Ship Later', 10: 'Waiting', 11: 'Overseas', 12: 'Delivery Issue', 13: 'Payment Received' };

function itemsText(items) {
    return (items || [])
        .map((l) => `${l.title || 'Item'}${[l.colorName, l.Size].filter((v) => v && !/^(no|null|undefined|SizeNull)$/i.test(String(v))).length ? ` (${[l.colorName, l.Size].filter((v) => v && !/^(no|null|undefined|SizeNull)$/i.test(String(v))).join(', ')})` : ''} × ${l.quantity || 1}`)
        .join(', ');
}

function ReceiveForm({ order, onDone }) {
    const [condition, setCondition] = useState('good');
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const save = () => {
        setSaving(true);
        myAxios
            .post('/returns/receive', { orderId: order._id, condition, amount, note })
            .then(() => {
                toast.success(condition === 'good' ? 'Received — stock is back' : 'Received as damaged — not added to stock');
                onDone();
            })
            .catch((e) => toast.error(e.response?.data?.message || 'Could not save'))
            .finally(() => setSaving(false));
    };
    return (
        <div className={styles.receive}>
            <div className={styles.condRow} role="radiogroup" aria-label="Parcel condition">
                <button type="button" className={`${styles.cond} ${condition === 'good' ? styles.condOnGood : ''}`} onClick={() => setCondition('good')}>
                    <strong>Good</strong>
                    <span>Back to stock</span>
                </button>
                <button type="button" className={`${styles.cond} ${condition === 'damaged' ? styles.condOnBad : ''}`} onClick={() => setCondition('damaged')}>
                    <strong>Damaged</strong>
                    <span>Not for sale</span>
                </button>
            </div>
            <div className={styles.formRow}>
                <label className={styles.fl}>
                    <span>Money received from the customer (optional)</span>
                    <input type="number" min="0" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 130 if they paid the delivery charge" />
                </label>
                <label className={styles.fl}>
                    <span>Note (optional)</span>
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. box opened, wrong size" />
                </label>
            </div>
            <button type="button" className={styles.primary} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Mark received'}
            </button>
        </div>
    );
}

function OrderCard({ o, open, setOpen, onChanged }) {
    const [undoing, setUndoing] = useState(false);
    const undo = () => {
        if (!window.confirm('Move this return back to "Waiting"?')) return;
        setUndoing(true);
        myAxios
            .post('/returns/undo', { orderId: o._id })
            .then(() => {
                toast.success('Moved back to waiting');
                onChanged();
            })
            .catch((e) => toast.error(e.response?.data?.message || 'Could not undo'))
            .finally(() => setUndoing(false));
    };
    return (
        <div className={styles.card}>
            <div className={styles.cardTop}>
                <div className={styles.cardMain}>
                    <div className={styles.cardTitle}>
                        <span className={styles.inv}>#{o.InvoiceId || String(o._id).slice(-6)}</span>
                        <span>{o.cName || 'Customer'}</span>
                        <span className={styles.muted}>{o.cMobile}</span>
                    </div>
                    <div className={styles.items}>{itemsText(o.items) || '—'}</div>
                    <div className={styles.meta}>
                        <span>{tk(o.totalPrice)}</span>
                        <span>Ordered {day(o.date)}</span>
                        {o.courier && <span className={styles.badge}>{COURIER[o.courier.status] || `Steadfast: ${o.courier.status}`}</span>}
                        {Number(o.orderStatus) !== 6 && <span className={styles.badgeWarn}>Status: {STATUS[o.orderStatus] || o.orderStatus}</span>}
                        {o.returnedBy && !o.received && <span className={styles.muted}>Marked return by {o.returnedBy}</span>}
                    </div>
                    {o.received && (
                        <div className={styles.meta}>
                            <span className={o.returnCondition === 'damaged' ? styles.badgeBad : styles.badgeGood}>
                                {o.returnCondition === 'damaged' ? 'Damaged' : 'Good'} · received {day(o.returnReceivedAt)}
                                {o.returnBy ? ` by ${o.returnBy}` : ''}
                            </span>
                            {o.ReturnAmount > 0 && <span>Collected {tk(o.ReturnAmount)}</span>}
                            {o.returnNote && <span className={styles.muted}>“{o.returnNote}”</span>}
                        </div>
                    )}
                </div>
                <div className={styles.cardAct}>
                    {o.received ? (
                        <button type="button" className={styles.ghost} onClick={undo} disabled={undoing}>
                            {undoing ? '…' : 'Undo'}
                        </button>
                    ) : (
                        <button type="button" className={open ? styles.ghost : styles.primarySm} onClick={() => setOpen(open ? null : o._id)}>
                            {open ? 'Close' : 'Receive'}
                        </button>
                    )}
                </div>
            </div>
            {open && !o.received && (
                <ReceiveForm
                    order={o}
                    onDone={() => {
                        setOpen(null);
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}

const Returns = () => {
    const [tab, setTab] = useState('waiting');
    const [period, setPeriod] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [found, setFound] = useState(null);
    const [searching, setSearching] = useState(false);
    const [open, setOpen] = useState(null);
    const inputRef = useRef(null);

    const load = useCallback(() => {
        setLoading(true);
        myAxios
            .get(`/returns?tab=${tab}&days=${period}`)
            .then((res) => setData(res.data))
            .catch((e) => toast.error(e.response?.data?.message || 'Could not load returns'))
            .finally(() => setLoading(false));
    }, [tab, period]);

    useEffect(() => {
        load();
    }, [load]);

    const search = (e) => {
        e?.preventDefault();
        if (q.trim().length < 4) return toast.warning('Type the invoice number, mobile or tracking code');
        setSearching(true);
        myAxios
            .post('/returns/find', { q })
            .then((res) => {
                setFound(res.data.orders || []);
                if ((res.data.orders || []).length === 1 && !res.data.orders[0].received) setOpen(res.data.orders[0]._id);
            })
            .catch((err) => toast.error(err.response?.data?.message || 'Search failed'))
            .finally(() => setSearching(false));
    };

    const changed = () => {
        load();
        if (found) {
            myAxios.post('/returns/find', { q }).then((res) => setFound(res.data.orders || [])).catch(() => {});
        }
    };

    const s = data?.summary || {};
    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}>
                        <AiOutlineRollback />
                    </div>
                    <div>
                        <h1 className={styles.title}>Returns</h1>
                        <p className={styles.subtitle}>Parcels coming back from the courier. Mark each one received when it reaches you.</p>
                    </div>
                </div>
                <select className={styles.period} value={period} onChange={(e) => setPeriod(Number(e.target.value))} aria-label="Period">
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            <div className={styles.stats}>
                <div className={styles.stat}>
                    <span>Waiting to receive</span>
                    <strong>{loading && !data ? '…' : s.waiting ?? 0}</strong>
                </div>
                <div className={styles.stat}>
                    <span>Received</span>
                    <strong>{s.received ?? 0}</strong>
                </div>
                <div className={styles.stat}>
                    <span>Damaged</span>
                    <strong className={s.damaged ? styles.red : ''}>{s.damaged ?? 0}</strong>
                </div>
                <div className={styles.stat}>
                    <span>Return rate</span>
                    <strong>{s.returnRate ?? 0}%</strong>
                    <small>
                        {s.returned ?? 0} of {s.shipped ?? 0} shipped
                    </small>
                </div>
                <div className={styles.stat}>
                    <span>Collected on returns</span>
                    <strong>{tk(s.collected)}</strong>
                </div>
            </div>

            <form className={styles.searchBox} onSubmit={search}>
                <div className={styles.searchLabel}>Receive a parcel</div>
                <div className={styles.searchRow}>
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Invoice number, mobile or Steadfast tracking code"
                        aria-label="Invoice number, mobile or tracking code"
                    />
                    <button type="submit" className={styles.primary} disabled={searching}>
                        <AiOutlineSearch /> {searching ? 'Finding…' : 'Find'}
                    </button>
                </div>
                {found && (
                    <div className={styles.found}>
                        {found.length === 0 ? (
                            <div className={styles.empty}>No order found for “{q}”.</div>
                        ) : (
                            found.map((o) => <OrderCard key={o._id} o={o} open={open === o._id} setOpen={setOpen} onChanged={changed} />)
                        )}
                    </div>
                )}
            </form>

            <div className={styles.tabs} role="tablist">
                <button type="button" role="tab" aria-selected={tab === 'waiting'} className={tab === 'waiting' ? styles.tabOn : styles.tab} onClick={() => setTab('waiting')}>
                    Waiting ({s.waiting ?? 0})
                </button>
                <button type="button" role="tab" aria-selected={tab === 'received'} className={tab === 'received' ? styles.tabOn : styles.tab} onClick={() => setTab('received')}>
                    Received ({s.received ?? 0})
                </button>
                <button type="button" className={styles.reload} onClick={load} title="Reload" aria-label="Reload">
                    <AiOutlineReload />
                </button>
            </div>

            <div className={styles.list}>
                {loading ? (
                    <div className={styles.empty}>Loading…</div>
                ) : !data?.orders?.length ? (
                    <div className={styles.empty}>
                        {tab === 'waiting'
                            ? 'Nothing waiting. Orders marked “Return” (or cancelled by Steadfast) show here until you receive them.'
                            : 'No returns received in this period.'}
                    </div>
                ) : (
                    data.orders.map((o) => <OrderCard key={o._id} o={o} open={open === o._id} setOpen={setOpen} onChanged={changed} />)
                )}
            </div>
        </div>
    );
};

export default Returns;
